const DEBOUNCE_DELAY = 300;

const CartStore = {
  cartItemCount: parseInt(document.querySelector('[data-cart-count]')?.textContent, 10) || 0,
  debounceTimers: {},
  pendingChanges: {},
  snapshots: {},
  requestGeneration: 0,
  pendingRequest: null,
  abortController: null,

  saveSnapshot(key, qty, badgeCount) {
    if (!this.snapshots[key]) {
      this.snapshots[key] = { qty, badgeCount };
    }
  },

  changeQuantity(key, quantity, sectionIds = [], source = '') {
    clearTimeout(this.debounceTimers[key]);
    delete this.pendingChanges[key];

    if (quantity === 0) {
      return this._executeChange(key, 0, sectionIds, source);
    }

    return new Promise((resolve, reject) => {
      this.pendingChanges[key] = { quantity, sectionIds, source, resolve, reject };
      this.debounceTimers[key] = setTimeout(() => {
        this._executePending();
      }, DEBOUNCE_DELAY);
    });
  },

  _executePending() {
    const batch = {};
    const callbacks = [];
    const sectionIdSet = new Set();
    let source = '';

    for (const [key, data] of Object.entries(this.pendingChanges)) {
      clearTimeout(this.debounceTimers[key]);
      this.debounceTimers[key] = null;

      const snapshot = this.snapshots[key];
      if (snapshot && data.quantity === snapshot.qty) {
        delete this.snapshots[key];
        data.resolve(null);
        continue;
      }

      batch[key] = data.quantity;
      callbacks.push(data);
      data.sectionIds.forEach((id) => sectionIdSet.add(id));
      if (!source) source = data.source;
    }

    this.pendingChanges = {};

    const keys = Object.keys(batch);
    if (keys.length === 0) return;

    const sectionIds = [...sectionIdSet];

    const promise = keys.length === 1
      ? this._executeChange(keys[0], batch[keys[0]], sectionIds, source)
      : this._executeBatchChange(batch, sectionIds, source);

    promise
      .then((data) => callbacks.forEach((cb) => cb.resolve(data)))
      .catch((err) => callbacks.forEach((cb) => cb.reject(err)));
  },

  async _executeBatchChange(updates, sectionIds, source) {
    const gen = ++this.requestGeneration;
    this._abortStaleRequest();

    try {
      const { signal } = this.abortController;
      const createRequest = () => CartAPI.update({ updates }, sectionIds, signal);

      this.pendingRequest = this._fetchWithRetry(createRequest);
      const data = await this.pendingRequest;

      if (gen !== this.requestGeneration) return data;

      updateCartCount(data.item_count ?? 0);
      for (const key of Object.keys(updates)) {
        delete this.snapshots[key];
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source,
        cartData: data,
        sections: data.sections,
      });

      return data;
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (gen !== this.requestGeneration) return;

      const firstSnapshot = Object.values(this.snapshots)[0];
      if (firstSnapshot) {
        updateCartCount(firstSnapshot.badgeCount);
      }
      for (const key of Object.keys(updates)) {
        delete this.snapshots[key];
      }

      publish(PUB_SUB_EVENTS.cartError, { source, error });
      throw error;
    } finally {
      if (gen === this.requestGeneration) {
        this.pendingRequest = null;
      }
    }
  },

  async _executeChange(key, quantity, sectionIds, source) {
    this.debounceTimers[key] = null;
    const gen = ++this.requestGeneration;
    this._abortStaleRequest();

    try {
      const { signal } = this.abortController;
      const createRequest = () => {
        const childKeys = this._findChildLineKeys(key);
        if (childKeys.length > 0) {
          const updates = { [key]: quantity };
          childKeys.forEach((ck) => { updates[ck] = quantity; });
          return CartAPI.update({ updates }, sectionIds, signal);
        }
        return CartAPI.change(key, quantity, sectionIds, signal);
      };

      this.pendingRequest = this._fetchWithRetry(createRequest);
      const data = await this.pendingRequest;

      if (gen !== this.requestGeneration) return data;

      updateCartCount(data.item_count ?? 0);
      delete this.snapshots[key];

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source,
        cartData: data,
        sections: data.sections,
      });

      return data;
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (gen !== this.requestGeneration) return;

      const snapshot = this.snapshots[key];
      if (snapshot) {
        updateCartCount(snapshot.badgeCount);
        delete this.snapshots[key];
      }

      publish(PUB_SUB_EVENTS.cartError, { source, error });
      throw error;
    } finally {
      if (gen === this.requestGeneration) {
        this.pendingRequest = null;
      }
    }
  },

  _abortStaleRequest() {
    this.abortController?.abort();
    this.abortController = new AbortController();
  },

  async _fetchWithRetry(fetchFn, retries = 1) {
    try {
      return await Promise.race([
        fetchFn(),
        cartTimeoutPromise(CART_API_TIMEOUT),
      ]);
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      if (error?.status === 409 && retries > 0) {
        await new Promise((r) => setTimeout(r, 100));
        return this._fetchWithRetry(fetchFn, retries - 1);
      }
      throw error;
    }
  },

  /**
   * Find nested child cart lines whose parent_line_key matches the given key.
   * Used to auto-sync quantity changes (parent → child) and to coordinate
   * removal. Reads from the DOM (data-parent-line-key emitted by cart-item.liquid
   * / main-cart-item.liquid) so it reflects the currently-rendered cart.
   *
   * Query is scoped to the containing cart (cart-drawer or [data-cart-items])
   * so if both cart drawer and cart page coexist in the DOM — e.g. during a
   * brief morph/render window — children aren't cross-matched.
   */
  _findChildLineKeys(parentKey) {
    if (!parentKey) return [];
    const parentItem = document.querySelector(
      `.cart-item[data-line-key="${parentKey}"]`
    );
    const root = parentItem?.closest('cart-drawer, [data-cart-items]') ?? document;
    const children = root.querySelectorAll(
      `[data-child-line][data-parent-line-key="${parentKey}"]`
    );
    return [...children]
      .map((el) => el.dataset.lineKey)
      .filter(Boolean);
  },

  async flushPending(sectionIds = []) {
    const promises = [];

    const pendingKeys = Object.keys(this.pendingChanges);
    if (pendingKeys.length > 0) {
      const updates = {};
      for (const [key, data] of Object.entries(this.pendingChanges)) {
        clearTimeout(this.debounceTimers[key]);
        this.debounceTimers[key] = null;
        updates[key] = data.quantity;
      }
      this.pendingChanges = {};
      promises.push(
        Promise.race([CartAPI.update({ updates }, sectionIds), cartTimeoutPromise(CART_API_TIMEOUT)])
      );
    }

    if (this.pendingRequest) {
      promises.push(this.pendingRequest);
    }

    if (promises.length) {
      try {
        await Promise.race([
          Promise.allSettled(promises),
          cartTimeoutPromise(CART_API_TIMEOUT),
        ]);
      } catch (flushError) {
        console.warn('[CartStore] flushPending exceeded timeout:', flushError);
      }
    }
  },

  hasPendingChanges() {
    return (
      Object.keys(this.pendingChanges).length > 0 ||
      Object.values(this.debounceTimers).some((t) => t !== null)
    );
  },
};

(() => {
  const _fetch = window.fetch.bind(window);

  window.fetch = async (url, options) => {
    const urlStr = typeof url === 'string' ? url : url instanceof Request ? url.url : '';

    if (/\/cart\.(js|json)(\?|$)/.test(urlStr)) {
      if (CartStore.hasPendingChanges() || CartStore.pendingRequest) {
        try {
          await CartStore.flushPending();
        } catch (error) {
          console.warn('[CartStore] Failed to flush pending changes before cart read:', error);
        }
      }
    }

    return _fetch(url, options);
  };
})();
